/**
 * Owner: Masa Bayu
 * File: paint.ts
 * Date:Time: 2026-10-15 08:20
 * Description: paintLineFromPdf · segments (LINE-02) · wordStarts · F1 fail-closed.
 */

import { paintSegmentsOut } from './segment-spacing.js';
import { computeWordSpacing } from './spacing.js';
import {
  isSegmentsJoinOk,
  isWordStartsCardinalityOk,
  logLinePaintCardinalityFail,
  paintTokens,
} from './tokens.js';
import type { LinePaintInput, LinePaintOutput, QXWLine } from './types.js';

function pt(n: number): string {
  return `${n}pt`;
}

function marksToFace(line: QXWLine): {
  readonly fontWeight?: string;
  readonly fontStyle?: string;
} {
  const marks = line.marks ?? [];
  let fontWeight: string | undefined;
  let fontStyle: string | undefined;
  for (const mark of marks) {
    if (mark.type === 'bold') fontWeight = '700';
    if (mark.type === 'italic') fontStyle = 'italic';
  }
  return {
    ...(fontWeight !== undefined ? { fontWeight } : {}),
    ...(fontStyle !== undefined ? { fontStyle } : {}),
  };
}

function paintDirection(direction: string | undefined): 'ltr' | 'rtl' | undefined {
  if (direction === 'rtl') return 'rtl';
  if (direction === 'ltr') return 'ltr';
  return undefined;
}

function baseOut(line: QXWLine): Omit<LinePaintOutput, 'paintMode'> {
  const { layout, typography, linePaint } = line;
  const face = marksToFace(line);
  const direction = paintDirection(linePaint?.direction);
  return {
    position: 'absolute',
    left: pt(layout.x),
    top: pt(layout.y),
    width: pt(layout.width),
    height: pt(layout.height),
    fontFamily: typography.family,
    fontSize: pt(typography.size),
    ...face,
    ...(direction !== undefined ? { direction } : {}),
    whiteSpace: 'pre',
    dataLineId: line.id,
  };
}

/**
 * Pure L2 line paint from tip.
 * NO text-align (G32 / Q11).
 * R11-LINE-02 segments primary under v2 · wordStarts kept for LINE-01 package path.
 */
export function paintLineFromPdf(input: LinePaintInput): LinePaintOutput {
  const { line, font } = input;
  const { layout, typography, linePaint } = line;
  const out = baseOut(line);

  // F2.1 / R11.7e · segment paint when join matches
  if (
    linePaint?.extension === 'R11-LINE-02' &&
    linePaint.segments !== undefined &&
    isSegmentsJoinOk(line.text, linePaint.segments)
  ) {
    return {
      ...out,
      paintMode: 'segments',
      segments: paintSegmentsOut(
        linePaint.segments,
        font,
        typography.size,
      ),
    };
  }

  if (
    linePaint?.extension === 'R11-LINE-02' &&
    linePaint.segments !== undefined &&
    !isSegmentsJoinOk(line.text, linePaint.segments)
  ) {
    logLinePaintCardinalityFail({
      lineId: line.id,
      reason: 'SEGMENTS_JOIN_NE_TEXT',
      segmentsN: linePaint.segments.length,
    });
  }

  // F1 · strip unusable wordStarts (cardinality) before spacing / span emit
  let starts = linePaint?.wordStarts;
  if (starts !== undefined && !isWordStartsCardinalityOk(line.text, starts)) {
    logLinePaintCardinalityFail({
      lineId: line.id,
      reason: 'WORD_STARTS_NE_TOKEN_COUNT',
      wordStartsLen: starts.length,
      tokenCount: paintTokens(line.text).length,
    });
    starts = undefined;
  }

  const spacing = computeWordSpacing({
    text: line.text,
    bboxWidth: layout.width,
    ...(starts !== undefined ? { wordStarts: starts } : {}),
    ...(linePaint?.wordGaps !== undefined && starts !== undefined
      ? { wordGaps: linePaint.wordGaps }
      : {}),
    font: { family: typography.family, size: typography.size },
    ...(linePaint?.script !== undefined ? { script: linePaint.script } : {}),
  });

  const isCjk = linePaint?.script === 'cjk';
  const hasStarts = starts !== undefined && starts.length > 0;
  let paintMode: LinePaintOutput['paintMode'];
  if (isCjk) {
    paintMode = 'cjk-char';
  } else if (spacing.fastPathUsed) {
    paintMode = 'uniform-word-spacing';
  } else {
    paintMode = 'word-starts';
  }

  if (paintMode === 'uniform-word-spacing') {
    return {
      ...out,
      paintMode,
      wordSpacing: pt(spacing.wordSpacingPt),
      ...(hasStarts && starts !== undefined ? { wordStarts: starts } : {}),
    };
  }

  if (hasStarts && starts !== undefined) {
    return {
      ...out,
      paintMode,
      wordStarts: starts,
    };
  }

  return { ...out, paintMode };
}
